import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import {
    ClientCategoryPostModel,
    EntryCategoryPostModel,
    SubCategoryGroupPostModel,
    SubCategoryPostModel,
} from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

interface Props {
    subcategoryId?: number;
    clientCategoryId?: number;
    entryCategoryId?: number;
    subcategoryGroupId?: number;
}

export default function useCategoryData({
    subcategoryId,
    clientCategoryId,
    entryCategoryId,
    subcategoryGroupId,
}: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const getCategoryListQuery = useQuery({
        queryKey: [QueryKeys.GetCategories],
        queryFn: () => apis.engine.category.categoryCategoryListList(),
    });

    const getSubcategoryListQuery = useQuery({
        queryKey: [QueryKeys.GetSubcategoryList, { categoryId: entryCategoryId }],
        queryFn: () => apis.engine.category.categorySubCategoryListList({ categoryId: entryCategoryId }),
        enabled: entryCategoryId != undefined,
    });

    const getSubCategoryGroupsByCategoryIdQuery = useQuery({
        queryKey: [QueryKeys.GetSubCategoryGroupsByCategoryId, { entryCategoryId }],
        queryFn: () =>
            apis.engine.category.categoryGetSubCategoryGroupsByCategoryIdList({
                id: entryCategoryId,
            }),
        enabled: entryCategoryId != undefined,
    });

    const getSubCategoryGroupsBySubCategoryGroupIdQuery = useQuery({
        queryKey: [QueryKeys.GetSubCategoryGroupsByCategoryId, { subcategoryGroupId }],
        queryFn: () =>
            apis.engine.category.categoryGetSubCategoryGroupsBySubCategoryGroupIdList({
                id: subcategoryGroupId,
            }),
        enabled: subcategoryGroupId != undefined,
    });

    const getSubCategoryGroupsBySubCategoryIdQuery = useQuery({
        queryKey: [QueryKeys.GetSubCategoriesBySubCategoryId, { subcategoryId }],
        queryFn: () =>
            apis.engine.category.categorySubCategoriesBySubcategoryIdList({
                subCategoryId: subcategoryId,
            }),
        enabled: subcategoryId != undefined,
    });

    const getSortedSubCategoryQuery = useQuery({
        queryKey: [QueryKeys.GetSortedSubCategories],
        queryFn: () => apis.engine.category.categoryGetSortedSubCategoryList(),
    });

    const getSubCategoryQuery = useQuery({
        queryKey: [QueryKeys.GetSubCategory, { subcategoryId }],
        queryFn: () => apis.engine.category.categoryGetSubCategoryList({ id: subcategoryId }),
        enabled: subcategoryId != undefined,
    });

    const getSortedSubCategoryGroupsQuery = useQuery({
        queryKey: [QueryKeys.GetSortedSubCategoryGroups],
        queryFn: () => apis.engine.category.categoryGetSortedSubCategoryGroupList(),
    });

    const getSubCategoryGroupQuery = useQuery({
        queryKey: [QueryKeys.GetSubCategoryGroup, { subcategoryGroupId }],
        queryFn: () => apis.engine.category.categoryGetSubCategoryGroupList({ id: subcategoryGroupId }),
        enabled: subcategoryGroupId != undefined,
    });

    const getClientCategoryQuery = useQuery({
        queryKey: [QueryKeys.GetClientCategory, { clientCategoryId }],
        queryFn: () => apis.engine.category.categoryGetClientCategoryList({ id: clientCategoryId }),
        enabled: clientCategoryId != undefined,
    });

    const getClientCategoriesQuery = useQuery({
        queryKey: [QueryKeys.GetClientCategories],
        queryFn: () => apis.engine.category.categoryGetClientCategoriesList(),
    });

    const getEntryCategoryQuery = useQuery({
        queryKey: [QueryKeys.GetEntryCategory, { entryCategoryId }],
        queryFn: () => apis.engine.category.categoryGetEntryCategoryList({ id: entryCategoryId }),
        enabled: entryCategoryId != undefined,
    });

    const saveSubCategoryMutation = useMutation({
        mutationFn: (post: SubCategoryPostModel) => apis.engine.category.categorySaveSubCategoryCreate(post),
        onSuccess: (_, { subcategoryId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetSortedSubCategories] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetSubCategory, { subcategoryId }],
            });
            toast.success("Subcategory saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save subcategory"),
    });

    const saveClientCategoryMutation = useMutation({
        mutationFn: (post: ClientCategoryPostModel) => apis.engine.category.categorySaveClientCategoryCreate(post),
        onSuccess: (_, { clientCategoryId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetSortedClientCategory] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetClientCategory, { clientCategoryId }],
            });
            toast.success("Client category saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save client category"),
    });

    const saveEntryCategoryMutation = useMutation({
        mutationFn: (post: EntryCategoryPostModel) => apis.engine.category.categorySaveEntryCategoryCreate(post),
        onSuccess: (_, { categoryId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetSortedEntryCategory] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetEntryCategory, { entryCategoryId: categoryId }],
            });
            toast.success("Category saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save category"),
    });

    const saveSubCategoryGroupMutation = useMutation({
        mutationFn: (post: SubCategoryGroupPostModel) => apis.engine.category.categorySaveSubCategoryGroupCreate(post),
        onSuccess: (_, { subcategoryGroupId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetSortedSubCategoryGroups],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetSubCategoryGroup, { subcategoryGroupId }],
            });
            toast.success("Subcategory group saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save subcategory"),
    });

    return {
        categories: getCategoryListQuery,
        subcategoryList: getSubcategoryListQuery,
        subCategoryGroupsByCategoryId: getSubCategoryGroupsByCategoryIdQuery,
        subCategoryGroupsBySubCategoryId: getSubCategoryGroupsBySubCategoryGroupIdQuery,
        subCategoriesBySubCategoryId: getSubCategoryGroupsBySubCategoryIdQuery,
        subCategoryGroup: getSubCategoryGroupQuery,
        subCategories: getSortedSubCategoryQuery,
        subCategory: getSubCategoryQuery,
        subCategoryGroups: getSortedSubCategoryGroupsQuery,
        clientCategory: getClientCategoryQuery,
        clientCategories: getClientCategoriesQuery,
        entryCategory: getEntryCategoryQuery,

        saveSubcategory: saveSubCategoryMutation,
        saveClientCategory: saveClientCategoryMutation,
        saveEntryCategory: saveEntryCategoryMutation,
        saveSubCategoryGroup: saveSubCategoryGroupMutation,
    };
}
